import React, { useEffect, useMemo, useRef, useState } from "react";
import { Target, Check, Plus, X, CheckCircle2, Award, Pencil, ChevronDown } from "lucide-react";
import { apiFetch, useApiData } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import {
  KartaPrehledu, HlavickaDlazdice, SkupinaPrehledu, AkceHlavicky, Pilulka,
} from "./PrehledDlazdice.jsx";

// Priority měsíce (3 body). Definuje owner (edit + označit splněno), manažer vidí read-only.
// Po označení splněno se bonusy propíšou do HR (čeká na vyplacení).
// Volitelně navázané na metriku (cíl) — realtime progress + auto „cíl splněn".
const METRIC_LABELS = {
  none: "Bez metriky (ruční)",
  new_client_revenue: "Obrat z nových klientů (Kč)",
  monthly_revenue: "Měsíční obrat celkem (Kč)",
  new_clients_total: "Počet nových klientů (celkem)",
  new_clients_trener: "Počet nových klientů trenéra",
  deti_revenue: "Obrat z dětských permic (Kč)",
};
// Kratší popisky pro kompaktní progress řádek.
const METRIC_SHORT = {
  new_client_revenue: "Obrat noví klienti",
  monthly_revenue: "Měsíční obrat",
  new_clients_total: "Noví klienti",
  new_clients_trener: "Noví klienti trenéra",
  deti_revenue: "Obrat dětské permice",
};
const METRIC_IS_KC = (t) => t === "new_client_revenue" || t === "monthly_revenue" || t === "deti_revenue";

// Aktuální hodnota metriky. Pro aktuální měsíc bereme čísla z data-overview (sedí 1:1
// s Přehledem), pro zpětné měsíce fallback z měsíčního reportu. Per-trenér a děti z endpointu.
function metricCurrent(it, report, overview) {
  switch (it.metric_type) {
    case "new_client_revenue": return overview?.newClientRevenue ?? report?.newClientRevenue ?? null;
    case "monthly_revenue": return overview?.monthlyRevenueLive ?? report?.revenue ?? null;
    case "new_clients_total": return overview?.newThisMonth ?? report?.addedClients ?? null;
    case "new_clients_trener":
    case "deti_revenue": return it.current_server ?? null;
    default: return null;
  }
}

const MESICE = ["", "leden", "únor", "březen", "duben", "květen", "červen",
  "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
function popisMesice(klic) {
  if (!klic) return "";
  const [r, m] = String(klic).split("-").map(Number);
  return MESICE[m] ? `${MESICE[m]} ${r}` : "";
}

function emptyItems() {
  return [1, 2, 3].map((poradi) => ({ poradi, text: "", bonusy: [], splneno: false, hr_propsano: false, id: null, metric_type: "none", metric_target: null, metric_trener: "", current_server: null }));
}

function initials(name) {
  return String(name || "").split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

// `kompaktni` udělá z každé priority jeden řádek a bonusy, pruh postupu
// i tlačítka ukáže až po klepnutí. `pod` pustí dolů do stejné karty další
// obsah, CEO přehled tam dává cíle měsíce (14.9.2026).
export default function MesicniPriority({ mesic, reportData, highlight = false, nazev = "Priority měsíce", odznak, kompaktni = false, pod = null }) {
  const role = getCurrentUser()?.role || "";
  const isOwner = role === "owner";
  const [items, setItems] = useState(emptyItems());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [confirmState, setConfirmState] = useState(null); // designový confirm místo window.confirm
  const [rozbalena, setRozbalena] = useState(null);       // pořadí priority s otevřeným detailem
  const odznakMesice = odznak !== undefined ? odznak : (kompaktni ? popisMesice(mesic) : undefined);
  // Kompaktní zobrazení jako výchozí; editace se otevře kliknutím (jen owner).
  const [editing, setEditing] = useState(false);
  const autoOpened = useRef(false);

  // Když report není předán (např. v Přehledu), dotáhneme si měsíční data sami,
  // aby revenue/počet metriky seděly s reportem (fallback pro zpětné měsíce).
  const { data: selfReport } = useApiData(!reportData && mesic ? `/api/data-monthly?month=${mesic}` : null);
  const report = reportData || selfReport || null;

  // Pro aktuální měsíc bereme čísla z Přehledu (data-overview), aby priorita seděla 1:1
  // s tím, co uživatel vidí na Přehledu (např. revenue nových klientů).
  const nowKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const isCurrentMonth = mesic === nowKey;
  const { data: overview } = useApiData(isCurrentMonth ? "/api/data-overview" : null);

  const { data: hrData } = useApiData("/api/hr-list-people?filter=vse&status=aktivni");
  const people = useMemo(() => {
    const ppl = (hrData && hrData.people) || [];
    return ppl.map((p) => ({ id: p.id, name: p.display_name, isTrener: p.is_trener === true })).sort((a, b) => a.name.localeCompare(b.name, "cs"));
  }, [hrData]);
  const treneri = useMemo(() => people.filter((p) => p.isTrener), [people]);

  const load = async () => {
    if (!mesic) return;
    setLoading(true);
    try {
      const r = await apiFetch(`/api/mesicni-priority?mesic=${encodeURIComponent(mesic)}`);
      const j = await r.json();
      const byPoradi = {};
      (j.items || []).forEach((it) => { byPoradi[it.poradi] = it; });
      setItems([1, 2, 3].map((poradi) => byPoradi[poradi] || { poradi, text: "", bonusy: [], splneno: false, hr_propsano: false, id: null, metric_type: "none", metric_target: null, metric_trener: "", current_server: null }));
    } catch (_) { setItems(emptyItems()); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [mesic]);

  // Když owner ještě nic nevyplnil, otevři rovnou editaci (jednorázově po načtení).
  useEffect(() => {
    if (!loading && isOwner && !autoOpened.current) {
      autoOpened.current = true;
      if (!items.some((it) => it.text || (it.bonusy || []).length)) setEditing(true);
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const setText = (poradi, text) => setItems((arr) => arr.map((it) => it.poradi === poradi ? { ...it, text } : it));
  const addBonus = (poradi, personId, castka) => {
    const person = people.find((p) => p.id === personId);
    if (!person || !(Number(castka) > 0)) return;
    setItems((arr) => arr.map((it) => it.poradi === poradi
      ? { ...it, bonusy: [...it.bonusy.filter((b) => b.person_id !== personId), { person_id: personId, person_name: person.name, castka: Number(castka) }] }
      : it));
  };
  const removeBonus = (poradi, personId) => setItems((arr) => arr.map((it) => it.poradi === poradi ? { ...it, bonusy: it.bonusy.filter((b) => b.person_id !== personId) } : it));
  const setMetric = (poradi, patch) => setItems((arr) => arr.map((it) => it.poradi === poradi ? { ...it, ...patch } : it));

  const save = async () => {
    setSaving(true); setSavedMsg("");
    try {
      await apiFetch("/api/mesicni-priority", { method: "POST", body: JSON.stringify({ action: "save", mesic, items: items.map((it) => ({ poradi: it.poradi, text: it.text, bonusy: it.bonusy, metric_type: it.metric_type || "none", metric_target: it.metric_target, metric_trener: it.metric_trener || "" })) }) });
      setSavedMsg("Uloženo.");
      await load();
      setEditing(false);
      setTimeout(() => setSavedMsg(""), 2500);
    } catch (_) { setSavedMsg("Uložení selhalo."); }
    setSaving(false);
  };

  const complete = (it) => {
    if (!it.id) { setSavedMsg("Nejdřív prioritu ulož."); return; }
    const bonusCount = (it.bonusy || []).length;
    setConfirmState({
      title: "Označit prioritu jako splněnou?",
      description: bonusCount ? <>Tím se <strong>{bonusCount} bonus(ů)</strong> propíše do HR (čeká na vyplacení).</> : "Priorita se označí jako splněná.",
      confirmLabel: "Splněno",
      variant: "save",
      onConfirm: async () => {
        try {
          await apiFetch("/api/mesicni-priority", { method: "POST", body: JSON.stringify({ action: "complete", id: it.id }) });
          await load();
        } catch (_) { /* ticho */ }
      },
    });
  };
  // Feedback #62 (13.8.2026): uzavrit cil jako NESPLNENY (treti stav).
  const oznacNesplneno = (it) => {
    if (!it.id) { setSavedMsg("Nejdřív prioritu ulož."); return; }
    setConfirmState({
      title: "Označit prioritu jako nesplněnou?",
      description: "Cíl se uzavře jako nesplněný a uvidí to celý tým v meetingu. Jde to kdykoli zrušit.",
      confirmLabel: "Nesplněno",
      variant: "danger",
      onConfirm: async () => {
        try {
          await apiFetch("/api/mesicni-priority", { method: "POST", body: JSON.stringify({ action: "nesplneno", id: it.id }) });
          await load();
        } catch (_) { /* ticho */ }
      },
    });
  };
  const uncomplete = (it) => {
    if (!it.id) return;
    setConfirmState({
      title: "Zrušit splnění priority?",
      description: "Bonusy už vložené do HR tam zůstanou (vyřeš je v HR).",
      confirmLabel: "Zrušit splnění",
      variant: "warning",
      onConfirm: async () => {
        try {
          await apiFetch("/api/mesicni-priority", { method: "POST", body: JSON.stringify({ action: "uncomplete", id: it.id }) });
          await load();
        } catch (_) { /* ticho */ }
      },
    });
  };

  // Skeleton — box se ukáže okamžitě, obsah doskočí (žádný pop-in).
  if (loading) {
    return (
      <KartaPrehledu>
        <HlavickaDlazdice ikona={Target} nazev={nazev} odznak={odznakMesice} />
        <div className="space-y-2 p-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl border border-slate-100 bg-slate-50" />)}
        </div>
      </KartaPrehledu>
    );
  }
  const anyContent = items.some((it) => it.text || (it.bonusy || []).length);
  if (!isOwner && !anyContent) return null; // manažerovi neukazuj prázdný blok

  const splnenych = items.filter((it) => it.splneno).length;
  const vyplnenych = items.filter((it) => it.text).length || items.length;
  const bonusyCelkem = items.reduce((s, it) => s + (it.bonusy || []).reduce((x, b) => x + (Number(b.castka) || 0), 0), 0);

  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={Target}
        nazev={nazev}
        odznak={odznakMesice}
        akce={isOwner ? (editing ? (
          <span className="flex shrink-0 items-center gap-1.5">
            <AkceHlavicky popis="Zavřít" onClick={() => { load(); setEditing(false); }}>Zavřít</AkceHlavicky>
            <AkceHlavicky ikona={Check} popis="Uložit" onClick={save} disabled={saving}>{saving ? "Ukládám…" : "Uložit"}</AkceHlavicky>
          </span>
        ) : (
          <AkceHlavicky ikona={Pencil} popis="Upravit priority" onClick={() => setEditing(true)} />
        )) : undefined}
      />

      <div className="px-4 py-4 sm:px-5">
        <div className="flex items-baseline gap-2 whitespace-nowrap">
          <span className="text-3xl font-bold leading-none tabular-nums text-slate-900 sm:text-4xl">{splnenych} z {vyplnenych}</span>
          <span className="text-lg font-semibold text-slate-500">splněno</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {!kompaktni && <Pilulka>3 cíle a bonus</Pilulka>}
          {bonusyCelkem > 0 && <Pilulka>bonusy {Math.round(bonusyCelkem).toLocaleString("cs")} Kč</Pilulka>}
          {savedMsg && <span className="inline-flex items-center rounded-lg bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">{savedMsg}</span>}
        </div>
      </div>

      <SkupinaPrehledu ikona={CheckCircle2} nazev={kompaktni ? "Priority" : "Cíle"} />
      <div className="divide-y divide-slate-200 px-4 sm:px-5">
        {items.map((it) => {
          const bonusTotal = (it.bonusy || []).reduce((s, b) => s + (Number(b.castka) || 0), 0);
          const hasMetric = it.metric_type && it.metric_type !== "none";
          const target = it.metric_target != null ? Number(it.metric_target) : null;
          const current = metricCurrent(it, report, isCurrentMonth ? overview : null);
          const isKc = METRIC_IS_KC(it.metric_type);
          const fmt = (v) => (v == null ? "—" : isKc ? `${Math.round(v).toLocaleString("cs")} Kč` : Math.round(v).toLocaleString("cs"));
          const pct = hasMetric && target ? Math.min(100, Math.round(((Number(current) || 0) / target) * 100)) : 0;
          const reached = hasMetric && target != null && current != null && Number(current) >= target;

          // Zobrazení (default). Text priority má celou šířku, číslo metriky je
          // vpravo na stejné ose jako v ostatních dlaždicích, pruh postupu je pod
          // textem a tlačítka mají vlastní řádek.
          if (!(isOwner && editing)) {
            const uzavrena = it.splneno || it.nesplneno;
            const barvaCisla = it.splneno
              ? "bg-emerald-100 text-emerald-700"
              : it.nesplneno ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700";

            // Kompaktní řádek. Dlaždice v přehledu je úzká, takže je vidět jen
            // pořadí, text a procento. Bonusy, pruh postupu a tlačítka se
            // rozbalí klepnutím (14.9.2026).
            if (kompaktni) {
              const otevrena = rozbalena === it.poradi;
              const maCoRozbalit = (it.bonusy || []).length > 0 || (hasMetric && target != null) || (isOwner && it.id);
              return (
                <div key={it.poradi} className="py-1">
                  <button
                    type="button"
                    onClick={() => maCoRozbalit && setRozbalena(otevrena ? null : it.poradi)}
                    aria-expanded={maCoRozbalit ? otevrena : undefined}
                    className={`flex w-full items-center gap-2.5 py-1.5 text-left ${maCoRozbalit ? "" : "cursor-default"}`}
                  >
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${barvaCisla}`}>
                      {it.splneno ? <CheckCircle2 className="h-3.5 w-3.5" /> : it.poradi}
                    </span>
                    <span className={`min-w-0 flex-1 truncate text-sm font-semibold ${it.splneno ? "text-emerald-900 line-through decoration-emerald-300" : "text-slate-800"}`}>
                      {it.text || <span className="font-normal italic text-slate-400">nevyplněno</span>}
                    </span>
                    {it.splneno ? (
                      <span className="shrink-0 rounded-lg bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-800">Splněno</span>
                    ) : it.nesplneno ? (
                      <span className="shrink-0 rounded-lg bg-rose-100 px-2 py-0.5 text-xs font-bold text-rose-800">Nesplněno</span>
                    ) : hasMetric && target != null ? (
                      <span className={`shrink-0 text-sm font-bold tabular-nums ${reached ? "text-emerald-700" : "text-slate-900"}`}>{pct} %</span>
                    ) : null}
                    {maCoRozbalit && (
                      <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${otevrena ? "" : "-rotate-90"}`} />
                    )}
                  </button>

                  {hasMetric && target != null && (
                    <div className="ml-[34px] mt-1 h-1.5 overflow-hidden rounded-full bg-slate-200">
                      <div className={`h-full rounded-full transition-all ${
                        it.splneno ? "bg-emerald-500" : it.nesplneno ? "bg-slate-300" : reached ? "bg-emerald-500" : "bg-amber-400"
                      }`} style={{ width: `${pct}%` }} />
                    </div>
                  )}

                  {otevrena && (
                    <div className="ml-[34px] mt-2 space-y-2 pb-1">
                      {hasMetric && target != null && (
                        <div className="text-xs font-semibold tabular-nums text-slate-500">{fmt(current)} z {fmt(target)}</div>
                      )}
                      {(it.bonusy || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {(it.bonusy || []).map((b, i) => (
                            <span key={i} className="rounded-lg bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700">
                              {b.person_name} {Math.round(Number(b.castka) || 0).toLocaleString("cs")} Kč
                            </span>
                          ))}
                        </div>
                      )}
                      {isOwner && it.id && (
                        <div className="flex gap-2">
                          {uzavrena ? (
                            <button type="button" onClick={() => uncomplete(it)} className="btn btn-secondary btn-sm">
                              {it.splneno ? "Zrušit splnění" : "Zrušit uzavření"}
                            </button>
                          ) : (
                            <>
                              <button type="button" onClick={() => complete(it)} className="btn btn-primary btn-sm">
                                <Check className="h-3.5 w-3.5" /> Splněno
                              </button>
                              <button type="button" onClick={() => oznacNesplneno(it)} className="btn btn-secondary btn-sm">
                                Nesplněno
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={it.poradi} className="py-3">
                <div className="flex items-start gap-2.5">
                  <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${barvaCisla}`}>
                    {it.splneno ? <CheckCircle2 className="h-3.5 w-3.5" /> : it.poradi}
                  </span>
                  <span className={`flex-1 text-sm font-semibold leading-snug ${it.splneno ? "text-emerald-900 line-through decoration-emerald-300" : "text-slate-800"}`}>
                    {it.text || <span className="font-normal italic text-slate-400">nevyplněno</span>}
                  </span>
                  {it.splneno ? (
                    <span className="shrink-0 rounded-lg bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-800">Splněno</span>
                  ) : it.nesplneno ? (
                    <span className="shrink-0 rounded-lg bg-rose-100 px-2 py-0.5 text-xs font-bold text-rose-800">Nesplněno</span>
                  ) : hasMetric && target != null ? (
                    <span className={`shrink-0 whitespace-nowrap text-base font-bold tabular-nums ${reached ? "text-emerald-700" : "text-slate-900"}`}>
                      {fmt(current)} <span className="text-sm font-medium text-slate-400">/ {fmt(target)}</span>
                      {reached && <CheckCircle2 className="-mt-0.5 ml-1 inline-block h-3.5 w-3.5 text-emerald-600" />}
                    </span>
                  ) : null}
                </div>

                {hasMetric && target != null && (
                  uzavrena ? (
                    <div className="ml-[34px] mt-2 flex items-center gap-2">
                      <span className="text-sm font-bold tabular-nums text-slate-500">{fmt(current)} / {fmt(target)}</span>
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
                        <span className={`block h-full rounded-full ${it.splneno ? "bg-emerald-500" : "bg-slate-300"}`} style={{ width: `${pct}%` }} />
                      </span>
                    </div>
                  ) : (
                    <div className="ml-[34px] mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
                      <div className={`h-full rounded-full transition-all ${reached ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
                    </div>
                  )
                )}

                {(it.bonusy || []).length > 0 && (
                  <div className="ml-[34px] mt-2 flex flex-wrap gap-1.5">
                    {(it.bonusy || []).map((b, i) => (
                      <span key={i} className="rounded-lg bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700">
                        {b.person_name} {Math.round(Number(b.castka) || 0).toLocaleString("cs")} Kč
                      </span>
                    ))}
                  </div>
                )}

                {isOwner && it.id && (
                  <div className="ml-[34px] mt-2.5 flex gap-2">
                    {uzavrena ? (
                      <button type="button" onClick={() => uncomplete(it)} className="btn btn-secondary">
                        {it.splneno ? "Zrušit splnění" : "Zrušit uzavření"}
                      </button>
                    ) : (
                      <>
                        <button type="button" onClick={() => complete(it)} className="btn btn-primary flex-1 sm:flex-none">
                          <Check className="h-3.5 w-3.5" /> Splněno
                        </button>
                        <button type="button" onClick={() => oznacNesplneno(it)} className="btn btn-secondary flex-1 sm:flex-none">
                          Nesplněno
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          }

          return (
          <div key={it.poradi} className="py-3">
            <div>
              {/* Řádek 1: číslo + text + Splněno */}
              <div className="flex items-center gap-2.5">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${it.splneno ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {it.splneno ? <CheckCircle2 className="w-4 h-4" /> : it.poradi}
                </div>
                <div className="flex-1 min-w-0">
                  {isOwner && !it.splneno ? (
                    <input
                      className="field w-full font-medium"
                      value={it.text}
                      onChange={(e) => setText(it.poradi, e.target.value)}
                      placeholder={`Priorita ${it.poradi}…`}
                    />
                  ) : (
                    <div className={`text-sm font-semibold leading-snug ${it.splneno ? "text-emerald-900" : "text-slate-800"}`}>{it.text || <span className="text-slate-400 italic font-normal">nevyplněno</span>}</div>
                  )}
                </div>
                {isOwner ? (
                  (it.splneno || it.nesplneno)
                    ? <button onClick={() => uncomplete(it)} className="inline-flex min-h-[44px] items-center text-xs text-slate-400 hover:text-slate-700 flex-shrink-0 sm:min-h-0">Zrušit</button>
                    : <>
                      <button onClick={() => complete(it)} className="btn btn-primary"><Check className="w-3.5 h-3.5" /> Splněno</button>
                      <button onClick={() => oznacNesplneno(it)} className="min-h-[44px] text-xs font-semibold text-rose-600 hover:text-rose-800 border border-rose-200 rounded-lg px-2 py-1.5 flex-shrink-0 sm:min-h-0">Nesplněno</button>
                    </>
                ) : it.splneno ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 flex-shrink-0"><CheckCircle2 className="w-3.5 h-3.5" /> Splněno</span>
                ) : null}
              </div>

              {/* Řádek 2: metrika / cíl (jen owner při editaci) */}
              {isOwner && !it.splneno && (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap pl-8">
                  <select
                    value={it.metric_type || "none"}
                    onChange={(e) => setMetric(it.poradi, { metric_type: e.target.value, metric_trener: e.target.value === "new_clients_trener" ? it.metric_trener : "" })}
                    className="field w-auto"
                  >
                    {Object.entries(METRIC_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {hasMetric && (
                    <input
                      type="number"
                      value={it.metric_target ?? ""}
                      onChange={(e) => setMetric(it.poradi, { metric_target: e.target.value === "" ? null : Number(e.target.value) })}
                      placeholder={isKc ? "cíl (Kč)" : "cíl"}
                      className="field w-24"
                    />
                  )}
                  {it.metric_type === "new_clients_trener" && (
                    <select
                      value={it.metric_trener || ""}
                      onChange={(e) => setMetric(it.poradi, { metric_trener: e.target.value })}
                      className="field w-auto max-w-[160px]"
                    >
                      <option value="">+ trenér…</option>
                      {treneri.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )}
                </div>
              )}

              {/* Progress bar (cíl navázaný na číslo v systému) */}
              {hasMetric && target != null && (
                <div className="mt-2 pl-8">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <span className="text-[11px] font-medium text-slate-400 truncate">{METRIC_SHORT[it.metric_type] || METRIC_LABELS[it.metric_type]}</span>
                    <span className={`text-xs font-bold whitespace-nowrap ${reached ? "text-emerald-700" : "text-slate-800"}`}>
                      {fmt(current)} <span className="text-slate-400 font-medium">/ {fmt(target)}</span>
                      {reached && <CheckCircle2 className="inline-block ml-1 w-3.5 h-3.5 -mt-0.5 text-emerald-600" />}
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${reached ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )}

              {/* Bonus blok. 14. 9. 2026 (fáze 6): tlačítka Zrušit, Nesplněno a Bonus mají na telefonu 44 px, křížek u bonusu neviditelnou plochu (.dotyk-rozsiren). */}
              {((it.bonusy || []).length > 0 || (isOwner && !it.splneno)) && (
                <div className="mt-2 pt-2 pl-8 border-t border-slate-100">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Award className="w-3.5 h-3.5 text-brand-400 flex-shrink-0" />
                    {(it.bonusy || []).map((b) => (
                      <span key={b.person_id} className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded-full pl-0.5 pr-2 py-0.5">
                        <span className="w-5 h-5 rounded-full bg-white text-brand-600 flex items-center justify-center text-[11px] font-bold">{initials(b.person_name)}</span>
                        {b.person_name}, {Math.round(b.castka).toLocaleString("cs")}
                        {isOwner && !it.splneno && (
                          <button onClick={() => removeBonus(it.poradi, b.person_id)} className="dotyk-rozsiren w-3.5 h-3.5 flex items-center justify-center text-brand-300 hover:text-rose-600"><X className="w-2.5 h-2.5" /></button>
                        )}
                      </span>
                    ))}
                    {bonusTotal > 0 && <span className="text-[11px] font-semibold text-brand-700 bg-brand-100 rounded-full px-1.5 py-0.5">Σ {Math.round(bonusTotal).toLocaleString("cs")} Kč</span>}
                    {(it.bonusy || []).length === 0 && !isOwner && <span className="text-xs text-slate-400">bez bonusu</span>}
                    {isOwner && !it.splneno && <BonusAdder people={people} onAdd={(pid, c) => addBonus(it.poradi, pid, c)} />}
                  </div>
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>
      {isOwner && editing && <div className="px-4 pb-3 text-[11px] text-slate-400 sm:px-5">Po označení „Splněno" se bonusy propíšou do HR (čeká na vyplacení).</div>}

      {pod}

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title || ""}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel || "Potvrdit"}
        variant={confirmState?.variant || "warning"}
        onConfirm={() => { const fn = confirmState?.onConfirm; setConfirmState(null); if (fn) fn(); }}
        onCancel={() => setConfirmState(null)}
      />
    </KartaPrehledu>
  );
}

function BonusAdder({ people, onAdd }) {
  const [pid, setPid] = useState("");
  const [castka, setCastka] = useState("");
  return (
    <span className="inline-flex items-center gap-1">
      <select value={pid} onChange={(e) => setPid(e.target.value)} className="field w-auto max-w-[150px]">
        <option value="">+ osoba…</option>
        {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <input type="number" value={castka} onChange={(e) => setCastka(e.target.value)} placeholder="Kč" className="field w-16" />
      <button
        type="button"
        onClick={() => { if (pid && Number(castka) > 0) { onAdd(pid, Number(castka)); setPid(""); setCastka(""); } }}
        disabled={!pid || !(Number(castka) > 0)}
        className="inline-flex min-h-[44px] items-center gap-0.5 text-xs font-semibold text-amber-700 border border-amber-200 rounded-lg px-1.5 py-1 hover:bg-amber-50 disabled:opacity-40 sm:min-h-0"
      >
        <Plus className="w-3 h-3" /> Bonus
      </button>
    </span>
  );
}
